/**
 * ExperimentPanel
 * ---------------
 *
 * September 2026
 */
import "./ExperimentPanel.css";

import * as React from "react";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";

import {
  MAX_FACTORIAL_LEVELS,
  getNominalConstantValue,
  computeDefaultFactorialLevels,
  getLevelLabels,
} from "./FactorialLevels";
import { distOptions, distParams } from "./DistributionOptions";
import FactorialLevelDistributionOverlay from "./FactorialLevelDistributionOverlay";

/**
 * Configuration sub-panel for setting up simulation experiments.
 *
 * @param {Object} config                   - Configuration object for the simulation model.
 * @param {Function} setConfig              - Setter for config object.
 * @param {Array} selectedParamIds          - List of parameter identifiers selected for experimentation.
 * @param {Function} setSelectedParamIds    - Setter for selected parameter identifiers.
 * @param {Object} paramUpdates             - Updated values for selected parameters.
 * @param {Function} setParamUpdates        - Setter for paramUpdates.
 * @param {Array} availableParams           - List of parameters available for experimentation.
 * @param {Array} validSelectedParamIds     - List of selected parameter identifiers that are valid for experimentation.
 * @param {Array} selectedParameters        - List of selected parameter objects for experimentation.
 * @param {Function} setIsDistributionOverlayOpen - Notifies the parent when the factorial level distribution overlay opens/closes, so Run Simulation can be disabled while it's open.
 * @param {Array} availableResponseVariables - List of response variable options (e.g. Throughput) for Factorial experiments, each shaped { id, displayName }.
 */
function ExperimentPanel({
  config,
  setConfig,
  selectedParamIds,
  setSelectedParamIds,
  paramUpdates,
  setParamUpdates,
  availableParams,
  validSelectedParamIds,
  selectedParameters,
  numLevels,
  setNumLevels,
  setIsDistributionOverlayOpen,
  availableResponseVariables = [
    { id: "throughput", displayName: "Throughput" },
    { id: "completion_rate", displayName: "Completion rate" },
    { id: "entity_arrival_counts", displayName: "Entity arrival counts" },
    { id: "entity_total_time", displayName: "Entity total time" },
  ],
}) {
  // State hook to manage what element type to filter.
  const [filteredType, setFilter] = React.useState("none");

  // Memo hook tracking available parameters of filtered element type.
  const filteredParams = React.useMemo(() => {
    if (filteredType === "none") {return availableParams}
    
    return availableParams.filter(param => param.type === filteredType);
  }, [availableParams, filteredType]);

  // Memo hook tracking selected parameter ids of filtered element type.
  const filteredSelectedParamIds = React.useMemo(() => {
    const filteredParamIds = new Set(filteredParams.map((param) => param.id));
    return validSelectedParamIds.filter((id) => filteredParamIds.has(id));
  }, [filteredParams, validSelectedParamIds]);

  // Which factorial level's distribution editor overlay is currently open, if any.
  const [overlayTarget, setOverlayTarget] = React.useState(null); // { paramId, levelIndex } | null
  const levelOverlayRef = React.useRef(null);

  const isFactorial = config.experiment.type === "Factorial";
  const isDistributionType =
    config.experiment.type === "Univariate" ||
    config.experiment.type === "Monte Carlo";
  const levelLabels = getLevelLabels(numLevels);

  /**
   * Updates the experiment's name.
   *
   * @param {String} newName  - Updated experiment name.
   */
  const handleExperimentName = (newName) => {
    const newConfig = {
      ...config,
      experiment: { ...config.experiment, name: newName },
    };
    setConfig(newConfig);
  };

  /**
   * Updates the experiment type.
   *
   * @param {String} newType  - Updated experiment type.
   */
  const handleExperimentType = (newType) => {
    const newConfig = {
      ...config,
      experiment: { ...config.experiment, type: newType },
    };
    setConfig(newConfig);
  };

  /**
   * Updates the response variable (the metric, e.g. Throughput, that the
   * Factorial experiment's trials should be compared on).
   *
   * @param {String} newResponseVariable - Identifier of the selected response variable.
   */
  const handleResponseVariable = (newResponseVariable) => {
    const newConfig = {
      ...config,
      experiment: {
        ...config.experiment,
        responseVariable: newResponseVariable,
      },
    };
    setConfig(newConfig);
  };

  /**
   * Updates the selected parameters for experimentation.
   *
   * Fill in the level value
   *
   * @param {Array} ids - List of selected parameter identifiers.
   */
  const handleSelectedParameter = (ids) => {
    const filteredParamIds = new Set(filteredParams.map((param) => param.id));
    const hiddenSelectedIds = selectedParamIds.filter((id) => !filteredParamIds.has(id));
    const updatedSelectedParamIds = [...hiddenSelectedIds, ...ids];
    
    const newlyAddedIds = updatedSelectedParamIds.filter((id) => !selectedParamIds.includes(id));
    if (isFactorial && newlyAddedIds.length > 0) {
      setParamUpdates((prev) => {
        const updated = { ...prev };
        newlyAddedIds.forEach((id) => {
          if (updated[id]?.values) {
            return;
          }
          const param = availableParams.find((p) => p.id === id);
          const nominal = param ? getNominalConstantValue(param, config) : null;
          const defaultValues = computeDefaultFactorialLevels(
            nominal,
            numLevels,
          );
          if (defaultValues) {
            updated[id] = { ...updated[id], values: defaultValues };
          }
        });
        return updated;
      });
    }

    setSelectedParamIds(updatedSelectedParamIds);
  };

  /**
   * Updates the parameter values for experimentation.
   *
   * @param {Object} rowData - The parameter row data.
   * @param {String} bound - The bound to update ('min', 'max', or 'arg3').
   * @param {Number} value - The new value for the bound.
   */
  const handleParamChange = (rowData, bound, value) => {
    setParamUpdates((prev) => ({
      ...prev,
      [rowData.id]: { ...prev[rowData.id], [bound]: value },
    }));
  };

  /**
   * Updates the number of levels for a Factorial experiment.
   *
   *
   * @param {Number} newNumLevels - The updated number of levels.
   */
  const handleNumLevels = (newNumLevels) => {
    if (!newNumLevels || newNumLevels < 2) {
      newNumLevels = 2;
    }
    if (newNumLevels > MAX_FACTORIAL_LEVELS) {
      newNumLevels = MAX_FACTORIAL_LEVELS;
    }

    setParamUpdates((prev) => {
      const updated = { ...prev };
      for (const id of Object.keys(updated)) {
        const existingValues = updated[id].values || [];
        const hasDistLevel = existingValues.some(
          (level) => level !== null && typeof level === "object",
        );
        const param = availableParams.find((p) => p.id === id);
        const nominal = param ? getNominalConstantValue(param, config) : null;
        const defaultValues = hasDistLevel
          ? null
          : computeDefaultFactorialLevels(nominal, newNumLevels);

        const resizedValues =
          defaultValues ||
          Array.from(
            { length: newNumLevels },
            (_, i) => existingValues[i] ?? null,
          );
        updated[id] = { ...updated[id], values: resizedValues };
      }
      return updated;
    });

    setNumLevels(newNumLevels);
  };

  /**
   * Updates a single parameter's value for a specific factorial level.
   *
   * @param {Object} rowData    - The parameter row data.
   * @param {Number} levelIndex - Index of the level being edited.
   * @param {Number} value      - The new value for that level.
   */
  const handleLevelChange = (rowData, levelIndex, value) => {
    setParamUpdates((prev) => {
      const existingValues = prev[rowData.id]?.values
        ? [...prev[rowData.id].values]
        : Array(numLevels).fill(null);
      existingValues[levelIndex] = value;
      return {
        ...prev,
        [rowData.id]: { ...prev[rowData.id], values: existingValues },
      };
    });
  };

  /**
   * Switches a factorial level between a constant value and a distribution.
   *
   * @param {Object} rowData         - The parameter row data.
   * @param {Number} levelIndex      - Index of the level being toggled.
   * @param {Boolean} asDistribution - True to convert the level into a distribution, false for a constant.
   */
  const toggleLevelDistribution = (rowData, levelIndex, asDistribution) => {
    setParamUpdates((prev) => {
      const existingValues = prev[rowData.id]?.values
        ? [...prev[rowData.id].values]
        : Array(numLevels).fill(null);
      existingValues[levelIndex] = asDistribution
        ? { dist: "uniform", min: null, max: null, arg3: null, arg4: null }
        : null;
      return {
        ...prev,
        [rowData.id]: { ...prev[rowData.id], values: existingValues },
      };
    });
  };

  /**
   * Opens the distribution editor overlay for a factorial level, converting
   * the level into a distribution first if it's currently a constant.
   *
   * @param {Object} event      - The triggering click event.
   * @param {Object} rowData    - The parameter row data.
   * @param {Number} levelIndex - Index of the level being edited.
   */
  const openLevelOverlay = (event, rowData, levelIndex) => {
    const currentValue = rowData.values ? rowData.values[levelIndex] : null;
    if (!currentValue || typeof currentValue !== "object") {
      toggleLevelDistribution(rowData, levelIndex, true);
    }
    setOverlayTarget({ paramId: rowData.id, levelIndex });
    setIsDistributionOverlayOpen?.(true);
    levelOverlayRef.current?.toggle(event);
  };

  /**
   * Template for a parameter's constant value.
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const constantTemplate = (rowData, { rowIndex }) => {
    return (
      <InputNumber
        value={rowData.constant}
        onValueChange={(event) =>
          handleParamChange(rowData, "constant", event.value)
        }
        min={0} /* disabled={!(config.experiment.type === "Univariate")} */
        maxFractionDigits={2}
        inputStyle={{ width: "110px", marginTop: "1rem" }}
      />
    );
  };

  /**
   * Template for a parameter's probability distribution.
   *
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const distTemplate = (rowData, { rowIndex }) => {
    return (
      <Dropdown
        value={rowData.dist}
        onChange={(event) => handleParamChange(rowData, "dist", event.value)}
        options={distOptions}
        style={{ marginTop: "1rem" }}
      />
    );
  };

  /**
   * Template for a parameter's minumum value.
   *
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const minTemplate = (rowData, { rowIndex }) => {
    return (
      <InputNumber
        value={rowData.min}
        onValueChange={(event) =>
          handleParamChange(rowData, "min", event.value)
        }
        min={0}
        maxFractionDigits={2}
        disabled={!distParams[rowData.dist].min}
        inputStyle={{ width: "110px", marginTop: "1rem" }}
      />
    );
  };

  /**
   * Template for a parameter's maximum value.
   *
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const maxTemplate = (rowData, { rowIndex }) => {
    return (
      <InputNumber
        value={rowData.max}
        onValueChange={(event) =>
          handleParamChange(rowData, "max", event.value)
        }
        min={0}
        maxFractionDigits={2}
        disabled={!distParams[rowData.dist].max}
        inputStyle={{ width: "110px", marginTop: "1rem" }}
      />
    );
  };

  /**
   * Template for a parameter's 3rd argument value.
   *
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const arg3Template = (rowData, { rowIndex }) => {
    return (
      <FloatLabel style={{ marginTop: "1rem" }}>
        <InputNumber
          value={rowData.arg3}
          onValueChange={(event) =>
            handleParamChange(rowData, "arg3", event.value)
          }
          min={0}
          maxFractionDigits={2}
          disabled={!distParams[rowData.dist].arg3}
          inputStyle={{ width: "110px" }}
        />
        <label>{distParams[rowData.dist].arg3}</label>
      </FloatLabel>
    );
  };

  /**
   * Template for a parameter's 4th argument value.
   *
   * @param {Object} rowData  - The parameter row data.
   * @param {Object} rowIndex - The table row's index.
   */
  const arg4Template = (rowData, { rowIndex }) => {
    return (
      <FloatLabel style={{ marginTop: "1rem" }}>
        <InputNumber
          value={rowData.arg4}
          onValueChange={(event) =>
            handleParamChange(rowData, "arg4", event.value)
          }
          min={0}
          maxFractionDigits={2}
          disabled={!distParams[rowData.dist].arg4}
          inputStyle={{ width: "110px" }}
        />
        <label>{distParams[rowData.dist].arg4}</label>
      </FloatLabel>
    );
  };

  /**
   * Template factory for a factorial level's value column.
   * A level is either a constant number, or a distribution (same shape as
   * Univariate/Monte Carlo: dist/min/max/arg3/arg4). It's resolved fresh per
   * trial the same way those experiment types sample per trial. Constant
   * levels render a plain numeric input; distribution levels render a chip
   * naming the distribution, opening the shared editor overlay on click.
   *
   * @param {Number} levelIndex - Index of the level this column represents.
   */
  const levelTemplate = (levelIndex) => (rowData) => {
    const level = rowData.values ? rowData.values[levelIndex] : null;
    const isDistribution = level && typeof level === "object";

    if (isDistribution) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            marginTop: "1rem",
          }}
        >
          <Button
            label={level.dist || "uniform"}
            className="p-button-sm p-button-outlined"
            onClick={(event) => openLevelOverlay(event, rowData, levelIndex)}
          />
          <Button
            icon="pi pi-times"
            className="p-button-sm p-button-text"
            title="Use a constant value instead"
            onClick={() => toggleLevelDistribution(rowData, levelIndex, false)}
          />
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          marginTop: "1rem",
        }}
      >
        <InputNumber
          value={level}
          onValueChange={(event) =>
            handleLevelChange(rowData, levelIndex, event.value)
          }
          maxFractionDigits={2}
          inputStyle={{ width: "90px" }}
        />
        <Button
          icon="pi pi-chart-bar"
          className="p-button-sm p-button-text"
          title="Use a distribution instead"
          onClick={(event) => openLevelOverlay(event, rowData, levelIndex)}
        />
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div className="experiment-panel-form">
        {/* Experiment Name */}
        <div className="experiment-panel-row">
          <label htmlFor="experiment-name" className="experiment-panel-label">
            Experiment Name
          </label>

          <div className="experiment-panel-control">
            <InputText
              id="experiment-name"
              value={config.experiment.name}
              onChange={(event) => handleExperimentName(event.target.value)}
              placeholder="Enter name"
              className="experiment-panel-name"
            />
          </div>
        </div>

        {/* Experiment Type */}
        <div className="experiment-panel-row">
          <label htmlFor="experiment-type" className="experiment-panel-label">
            Experiment Type
          </label>

          <div className="experiment-panel-control">
            <Dropdown
              inputId="experiment-type"
              value={config.experiment.type}
              onChange={(event) => handleExperimentType(event.value)}
              options={["None", "Univariate", "Monte Carlo", "Factorial"]}
              className="experiment-panel-type"
            />
          </div>
        </div>

        {/* Number of Levels */}
        {isFactorial && (
          <div className="experiment-panel-row">
            <label
              htmlFor="number-of-levels"
              className="experiment-panel-label"
            >
              Number of Levels
            </label>

            <div className="experiment-panel-control">
              <InputNumber
                inputId="number-of-levels"
                value={numLevels}
                onValueChange={(event) => handleNumLevels(event.value)}
                min={2}
                max={MAX_FACTORIAL_LEVELS}
                showButtons
                className="experiment-panel-levels"
                inputStyle={{ width: "100%" }}
              />

              <span className="experiment-panel-calculation">
                {selectedParameters?.length || 0} parameter(s) × {numLevels}{" "}
                levels = {Math.pow(numLevels, selectedParameters?.length || 0)}{" "}
                trial(s)
              </span>
            </div>
          </div>
        )}

        {/* Response Variable */}
        {isFactorial && (
          <div className="experiment-panel-row">
            <label
              htmlFor="response-variable"
              className="experiment-panel-label"
            >
              Response Variable
            </label>

            <div className="experiment-panel-control">
              <Dropdown
                inputId="response-variable"
                value={config.experiment.responseVariable}
                onChange={(event) => handleResponseVariable(event.value)}
                options={availableResponseVariables}
                optionValue="id"
                optionLabel="displayName"
                placeholder="Select Response Variable"
                className="experiment-panel-response"
              />
            </div>
          </div>
        )}

        {/* Available Parameters */}
        <div className="experiment-panel-row">
          <label
            className="experiment-panel-label"
          >
            Available Parameters
          </label>

          <div className="experiment-panel-control">
            <MultiSelect
              inputId="available-parameters"
              value={filteredSelectedParamIds}
              onChange={(event) => handleSelectedParameter(event.value)}
              options={filteredParams}
              optionValue="id"
              optionLabel="displayName"
              filter
              placeholder="Select Parameters"
              className="experiment-panel-parameters"
            />
          </div>
        </div>
        
        {/* Parameter Filter */}
        <div className="experiment-panel-row">
          <label>Parameter Filter</label>
          
          <div className="experiment-panel-control">
            <Dropdown
              inputId="parameter-filter"
              value={filteredType}
              onChange={(event) => setFilter(event.value)}
              options={["none", "action", "resource", "entity_type"]}
              className="experiment-panel-type"
            />
          </div>
        </div>
      </div>

      <DataTable value={selectedParameters} scrollable scrollHeight="400px">
        <Column
          header="Element"
          field="name"
          sortable
          style={{ width: "25%" }}
        />

        <Column header="Type" field="type" />

        <Column header="Parameter" field="parameter" />

        {isFactorial &&
          levelLabels.map((label, levelIndex) => (
            <Column
              key={label}
              header={label}
              body={levelTemplate(levelIndex)}
            />
          ))}

        {isDistributionType && (
          <Column header="Constant" body={constantTemplate} />
        )}

        {isDistributionType && (
          <Column header="Distribution" body={distTemplate} />
        )}

        {isDistributionType && <Column header="Min" body={minTemplate} />}

        {isDistributionType && <Column header="Max" body={maxTemplate} />}

        {isDistributionType && <Column header="Arg 3" body={arg3Template} />}

        {isDistributionType && <Column header="Arg 4" body={arg4Template} />}
      </DataTable>

      <FactorialLevelDistributionOverlay
        ref={levelOverlayRef}
        overlayTarget={overlayTarget}
        selectedParameters={selectedParameters}
        numLevels={numLevels}
        setParamUpdates={setParamUpdates}
        onClose={() => setIsDistributionOverlayOpen?.(false)}
      />
    </div>
  );
}

export default ExperimentPanel;
